import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import Link from 'next/link';

export type ContentContactsPageProps = {
  infolinka: {
    email: {
      href: string;
      label: string;
    };
    phoneNumber: {
      href: string;
      label: string;
    };
  };
  operatingHours: string[];
};

export default function ContentContactsPage({ infolinka, operatingHours }: ContentContactsPageProps) {
  return (
    <>
      <div className='bg-surface mt-4 overflow-hidden shadow sm:rounded-lg'>
        <div className='px-4 py-5 sm:px-6'>
          <h2 className='text-content-focus text-lg font-medium leading-6'>Kontaktní informace</h2>
          <p className='text-content-suspend mt-1 max-w-2xl text-sm'>Kontaktní informace společnosti Blyth s.r.o.</p>
        </div>
        <div className='border-surface-divid border-t px-4 py-5 sm:p-0'>
          <dl className='divide-surface-divid divide-y'>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
              <dt className='text-content text-sm font-medium'>{'Registrace'}</dt>
              <dd className='text-content-focus mt-1 space-y-2 text-sm sm:col-span-2 sm:mt-0'>
                <p>IČO: 45803811</p>
                <p>DIČ: CZ45803811</p>
                <p>
                  Blyth s.r.o. je registrován pod značkou oddíl C, vložka 10996 ze dne 24.04.1992 u Městského soudu v
                  Praze.
                </p>
              </dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
              <dt className='text-content text-sm font-medium'>{'Provozní doba'}</dt>
              <dd className='text-content-focus mt-1 space-y-2 text-sm sm:col-span-2 sm:mt-0'>
                {operatingHours.map((str, idx) => (
                  <p key={`operatingHours-row-${idx}`}>{str}</p>
                ))}
              </dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
              <dt className='text-content text-sm font-medium'>Infolinka</dt>
              <dd className='text-content-focus mt-1 text-sm sm:col-span-2 sm:mt-0'>
                <dl className='space-y-2'>
                  <dt>
                    <span className='sr-only'>Telefon</span>
                  </dt>
                  <dd /* className='text-mask-contrast text-base' */>
                    <a className='hover:text-primary flex' href={infolinka.phoneNumber.href}>
                      <PhoneIcon aria-hidden='true' className='h-5 w-5 shrink-0' />
                      <span className='ml-3'>{infolinka.phoneNumber.label}</span>
                    </a>
                  </dd>
                  <dt>
                    <span className='sr-only'>Email</span>
                  </dt>
                  <dd /* className='text-mask-contrast text-base' */>
                    <a className='hover:text-primary flex' href={infolinka.email.href}>
                      <EnvelopeIcon aria-hidden='true' className='h-5 w-5 shrink-0' />
                      <span className='ml-3'>{infolinka.email.label}</span>
                    </a>
                  </dd>
                </dl>
              </dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
              <dt className='text-content text-sm font-medium'>{'Bankovní spojení'}</dt>
              <dd className='text-content-focus mt-1 space-y-2 text-sm sm:col-span-2 sm:mt-0'>
                <p>Číslo účtu: 1388056039/2700</p>
                <p>Název účtu: Blyth s.r.o.</p>
                <p>IBAN: CZ96 2700 0000 0013 8805 6039</p>
                <p>BIC (SWIFT) kód: BACXCZPP</p>
              </dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
              <dt className='text-content text-sm font-medium'>Provozovna</dt>
              <dd className='text-content-focus mt-1 text-sm sm:col-span-2 sm:mt-0'>
                <div>
                  Přátelství 1011/17, 104 00 Praha 10 - Uhříněves
                  <span className='ml-1 italic'>(POZOR: Vjezd do areálu z ulice Bečovská!!!)</span>
                </div>
                <ContentContactsPageGoogleMap className='mt-8 flex h-72 w-full md:h-96' />
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className='mt-16'>
        <h2 className='heading-section'>{'Obchodní zástupci'}</h2>
        <p className='mt-6'>
          {'Náš tým v plné kráse naleznete na stránce '}
          <Link className='link link-sharp link-bold' href={'/obchodni-tym'} prefetch={false}>
            {'obchodního týmu'}
          </Link>
          {'.'}
        </p>
      </div>
    </>
  );
}

function ContentContactsPageGoogleMap({ className }: { className?: string }) {
  // Coordinates of: Přátelství 1011/17, 104 00 Praha 10 - Uhříněves
  const position = {
    lat: 50.0404958,
    lng: 14.5872758,
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    id: 'google-map-script',
  });

  return (
    <div className={className}>
      {isLoaded && (
        <GoogleMap center={position} mapContainerStyle={{ height: '100%', width: '100%' }} zoom={17}>
          <Marker position={position} />
        </GoogleMap>
      )}
    </div>
  );
}
